import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import styled from "styled-components";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../models/User";
import {
	verifyStationGoLive,
	denyStationRequest,
	getStationRequestDestinations,
	getStationRequests,
	StationRequestRecord,
	StationRequestDestinationOption,
	StationRequestStatus,
	StationRequestType,
	updateStationRequest,
	guiLaiMatKhauTam,
	luuDiemChuoi,
	giaiLinkGoogleMapsChoDiem,
	uploadStationRequestFiles,
} from "../../../services/FormService";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import SearchPanel from "../User/component/SearchPanel";
import NumberPaginationBox from "../../controls/components/numberPaginationBox/NumberPaginationBox";
import useCommonListFunctions from "../../hooks/useCommonList/useCommonListFunctions";
import useAlertDialog from "../../hooks/useAlertDialog";
import { AlertType } from "../../hooks/common/useAttachAlertDialog";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { COUNTRIES, COUNTRY_BY_NAME } from "./countryData";
import {
	DIEM_RONG,
	DiemChuoi,
	O_DIEM,
	chuoiSanSangDuyet,
	danhSachDiem,
	diemConThieu,
	laHoSoChuoi,
	soDiemUocTinh,
	tenChuoi,
} from "./chuoiHelpers";
import { browseGeonamePlacesByCountry, searchGeonamePlacesForAdmin } from "../../../services/geonamePlaceSearchService";
import { resolveFrontDeskHoursRange, resolveStaffedHoursRange } from "./stationRequestHours";
import {
	getStationPortalLegalVerificationPanelEn,
	isStationPortalLegalVerificationCompleted,
	resolveStationRequestBusinessTypeDisplay,
	STATION_REQUEST_EDIT_BUSINESS_TYPE_OPTIONS,
} from "./stationPortalLegalHelpers";
import {
	resolveStationRequestBusinessName,
	resolveStationRequestRepresentativeName,
	resolveStationRequestRepresentativePhone,
	resolveStationRequestVenueName,
} from "./stationRequestIdentityHelpers";
import { normalizeCountryKey, toCanonicalCountryName } from "../CountryRates/countryCatalog";
import PermissionGate from "../../controls/auth/PermissionGate";
import safeExternalHref from "../../../utils/safeExternalHref";

const PER_PAGE = 200; // Queue thật ≤ ~71 (đo prod). Tải hết trong 1 trang để bộ lọc tab + ô chỉ số đếm ĐỦ, không bị cắt theo trang.

const PageWrap = styled(MyBookingWrapper)`
	padding: 24px 0 40px;
`;

const PageInner = styled.div`
	width: 1200px;
	max-width: 1440px;
	margin: 0 auto;

	@media screen and (max-width: 1282px) {
		width: 100%;
		padding: 0 16px;
	}
`;

const Hero = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 16px;
	margin-bottom: 24px;

	@media screen and (max-width: 768px) {
		flex-direction: column;
	}
`;

const HeroTitle = styled.div`
	color: #111827;
	font-size: 28px;
	font-weight: 700;
	line-height: 34px;
`;

const HeroSubtitle = styled.div`
	color: #6b7280;
	font-size: 15px;
	line-height: 22px;
	margin-top: 8px;
`;

const HeroMeta = styled.div`
	padding: 12px 16px;
	border-radius: 16px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	min-width: 180px;
`;

const HeroMetaLabel = styled.div`
	color: #6b7280;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

const HeroMetaValue = styled.div`
	color: #111827;
	font-size: 28px;
	font-weight: 700;
	line-height: 32px;
	margin-top: 8px;
`;

const HeroMetaText = styled.div`
	color: #6b7280;
	font-size: 13px;
	line-height: 18px;
	margin-top: 4px;
`;

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 24px;

	@media screen and (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media screen and (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

const StatCard = styled.div<{ $active?: boolean; $clickable?: boolean }>`
	padding: 16px 18px;
	border-radius: 18px;
	background: ${({ $active }) => ($active ? "#eff6ff" : "#ffffff")};
	border: 1px solid ${({ $active }) => ($active ? "#3b82f6" : "#e5e7eb")};
	box-shadow: 0 8px 30px rgba(15, 23, 42, 0.04);
	cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
	user-select: none;
	transition: border-color 0.12s ease, background 0.12s ease, transform 0.06s ease;
	&:hover {
		border-color: ${({ $clickable, $active }) =>
			$clickable ? ($active ? "#3b82f6" : "#c7d2fe") : "#e5e7eb"};
	}
	&:active {
		transform: ${({ $clickable }) => ($clickable ? "scale(0.99)" : "none")};
	}
`;

const StatLabel = styled.div`
	color: #6b7280;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

const StatValue = styled.div`
	color: #111827;
	font-size: 28px;
	font-weight: 700;
	line-height: 32px;
	margin-top: 10px;
`;

/**
 * Hai cột cuộn ĐỘC LẬP.
 *
 * Trước đây cả hai cột nằm trong dòng cuộn của trang, cột phải chỉ `position: sticky`:
 * muốn đọc hết hồ sơ bên phải thì phải cuộn qua hết danh sách bên trái trước. Nay mỗi
 * cột tự cuộn trong khung của mình, trang ngoài không cuộn nữa.
 *
 * `calc(100vh - 300px)` chừa chỗ cho tiêu đề trang và hàng thẻ số liệu ở trên.
 * Dưới 1100px thì xếp dọc và trả về cuộn trang như cũ — trên điện thoại hai vùng cuộn
 * lồng nhau là cực hình.
 */
const MainGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(320px, 0.95fr) minmax(420px, 1.45fr);
	gap: 20px;
	align-items: stretch;
	/* Cao hơn phần màn hình còn lại một chút, cố ý: trang vẫn cuộn nhẹ được nên
	   không bị cảm giác khoá cứng, mà hai cột vẫn đủ cao để đọc thoải mái. */
	height: calc(100vh - 170px);
	min-height: 620px;

	@media screen and (max-width: 1100px) {
		grid-template-columns: 1fr;
		height: auto;
	}
`;

const Panel = styled.div`
	border-radius: 20px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

const DetailPanel = styled(Panel)`
	display: flex;
	flex-direction: column;
	min-height: 0;

	@media screen and (max-width: 1100px) {
		max-height: none;
	}
`;

const PanelHeader = styled.div`
	padding: 18px 20px;
	border-bottom: 1px solid #f0f2f5;
`;

const PanelTitle = styled.div`
	color: #111827;
	font-size: 18px;
	font-weight: 700;
	line-height: 24px;
`;

const PanelSubtitle = styled.div`
	color: #6b7280;
	font-size: 13px;
	line-height: 18px;
	margin-top: 4px;
`;

const FilterWrap = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 150px;
	gap: 12px;
	padding: 16px 20px;
	border-bottom: 1px solid #f0f2f5;

	@media screen and (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const FilterSelect = styled.select`
	height: 44px;
	border-radius: 12px;
	border: 1px solid #d1d5db;
	padding: 0 14px;
	color: #111827;
	background: #ffffff;
	font-size: 14px;
	outline: none;
`;

const ListBody = styled.div`
	padding: 16px 20px 20px;
	overflow-y: auto;
	overscroll-behavior: contain;
	flex: 1;
	min-height: 0;

	/* Thanh cuộn LUÔN hiện — kể cả macOS/Safari (overlay scrollbar) và Chrome. Mấu chốt
	   là -webkit-appearance:none + TRACK CÓ MÀU + overflow-y:scroll (chừa sẵn máng), ép
	   Safari vẽ scrollbar cổ điển thay vì ẩn cho tới khi rê chuột (yêu cầu Quân 11/09). */
	overflow-y: scroll;
	scrollbar-width: thin;
	scrollbar-color: #cbd5e1 #eef2f6;
	&::-webkit-scrollbar {
		width: 12px;
		-webkit-appearance: none;
	}
	&::-webkit-scrollbar-track {
		background: #eef2f6;
		border-radius: 8px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c3ccd8;
		border-radius: 8px;
		border: 3px solid #eef2f6;
		min-height: 44px;
	}
	&::-webkit-scrollbar-thumb:hover {
		background: #94a3b8;
	}

	@media screen and (max-width: 1100px) {
		max-height: 60vh;
	}
`;

const RequestList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const RequestCard = styled.button<{ $selected?: boolean }>`
	width: 100%;
	text-align: left;
	padding: 16px;
	border-radius: 16px;
	border: 1px solid ${({ $selected }) => ($selected ? "#00a1ca" : "#e5e7eb")};
	background: ${({ $selected }) => ($selected ? "#f0fbff" : "#ffffff")};
	box-shadow: ${({ $selected }) => ($selected ? "0 12px 30px rgba(0, 161, 202, 0.14)" : "none")};
	cursor: pointer;
	transition: 0.2s ease;

	:hover {
		border-color: #00a1ca;
		background: #f8fdff;
	}
`;

const RequestCardTop = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 10px;
`;

const RequestTitle = styled.div`
	color: #111827;
	font-size: 16px;
	font-weight: 700;
	line-height: 22px;
`;

const RequestSub = styled.div`
	color: #6b7280;
	font-size: 13px;
	line-height: 18px;
	margin-top: 6px;
	word-break: break-word;
`;

const RequestMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px 10px;
	margin-top: 12px;
`;

const MetaChip = styled.div`
	padding: 6px 10px;
	border-radius: 999px;
	background: #f3f4f6;
	color: #374151;
	font-size: 12px;
	line-height: 16px;
`;

/** Queue row + detail header: portal legal registration complete vs still pending */
const LegalStatusChip = styled.span<{ $complete: boolean }>`
	display: inline-flex;
	align-items: center;
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	background: ${({ $complete }) => ($complete ? "#d1fae5" : "#fef3c7")};
	color: ${({ $complete }) => ($complete ? "#059669" : "#d97706")};
`;

const RequestTime = styled.div`
	color: #6b7280;
	font-size: 12px;
	line-height: 17px;
	margin-top: 12px;
`;

const StatusBadge = styled.div<{ color: string; background: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: fit-content;
	padding: 6px 10px;
	border-radius: 999px;
	background: ${({ background }) => background};
	color: ${({ color }) => color};
	font-size: 12px;
	font-weight: 700;
	line-height: 16px;
	white-space: nowrap;
`;

const Placeholder = styled.div`
	padding: 48px 24px;
	text-align: center;
	color: #6b7280;
	font-size: 14px;
	line-height: 22px;
`;

const DetailBody = styled.div`
	padding: 20px;
	overflow-y: auto;
	overscroll-behavior: contain;
	flex: 1;
	min-height: 0;

	@media screen and (max-width: 1100px) {
		max-height: none;
	}
`;

const DetailTop = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 14px;

	@media screen and (max-width: 640px) {
		flex-direction: column;
	}
`;

const DetailTitle = styled.div`
	color: #111827;
	font-size: 24px;
	font-weight: 700;
	line-height: 30px;
`;

const DetailMeta = styled.div`
	color: #6b7280;
	font-size: 13px;
	line-height: 18px;
	margin-top: 6px;
`;

const DetailLegalHint = styled.div`
	margin-top: 10px;
`;

const InfoGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
	margin-top: 20px;

	@media screen and (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media screen and (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

const InfoCard = styled.div`
	padding: 14px;
	border-radius: 14px;
	background: #f9fafb;
	border: 1px solid #eef2f7;
`;

const InfoLabel = styled.div`
	color: #6b7280;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
`;

const InfoValue = styled.div`
	color: #111827;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	margin-top: 8px;
	word-break: break-word;
`;

const Section = styled.div`
	margin-top: 24px;
	padding-top: 24px;
	border-top: 1px solid #f0f2f5;
`;

const SectionTitle = styled.div`
	color: #111827;
	font-size: 16px;
	font-weight: 700;
	line-height: 22px;
	margin-bottom: 14px;
`;

const KeyValueList = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px 18px;

	@media screen and (max-width: 720px) {
		grid-template-columns: 1fr;
	}
`;

const KeyValueItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const KeyLabel = styled.div`
	color: #6b7280;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
`;

const KeyValue = styled.div`
	color: #111827;
	font-size: 14px;
	line-height: 22px;
	word-break: break-word;
`;

const LinkValue = styled.a`
	color: #00a1ca;
	text-decoration: none;

	:hover {
		text-decoration: underline;
	}
`;

const ImageGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	margin-top: 14px;

	@media screen and (max-width: 640px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
`;

const ImageItem = styled.button`
	display: block;
	width: 100%;
	padding: 0;
	border-radius: 14px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
	background: #f9fafb;
	cursor: zoom-in;
`;

const Image = styled.img`
	display: block;
	width: 100%;
	height: 140px;
	object-fit: cover;
`;

const PreviewImage = styled.img`
	display: block;
	width: auto;
	max-width: min(92vw, 1400px);
	max-height: 92vh;
	object-fit: contain;
	border-radius: 18px;
	background: transparent;
	box-shadow: 0 24px 80px rgba(15, 23, 42, 0.45);
	cursor: zoom-out;
`;

const FileList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const FileItem = styled.a`
	display: inline-flex;
	align-items: center;
	width: fit-content;
	max-width: 100%;
	padding: 10px 12px;
	border-radius: 12px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	color: #111827;
	font-size: 13px;
	text-decoration: none;
	word-break: break-word;

	:hover {
		border-color: #00a1ca;
		color: #00a1ca;
	}
`;

const ActionWrap = styled.div`
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
`;

/**
 * Thanh hành động DÍNH ĐÁY cột chi tiết.
 *
 * Trước đây nó là mục cuối cùng bên trong vùng cuộn: hồ sơ dài thì phải cuộn hết mới
 * thấy nút Duyệt. Nay nó nằm ngoài vùng cuộn nên luôn ở đó, kèm một dòng nói rõ bấm
 * duyệt thì chuyện gì xảy ra — vì từ 28/08/2026 duyệt KHÔNG còn đưa điểm lên bán.
 */
/* Bảng điểm của chuỗi. Dùng lại đúng bảng màu và bo góc của các Panel khác. */
const KindTabs = styled.div`
	display: flex;
	gap: 6px;
	padding: 12px 20px 0;
`;

const KindTab = styled.button<{ $active?: boolean }>`
	appearance: none;
	font-size: 12.5px;
	font-weight: 700;
	padding: 7px 12px;
	border-radius: 10px;
	cursor: pointer;
	border: 1px solid ${({ $active }) => ($active ? "#0369a1" : "#e5e7eb")};
	background: ${({ $active }) => ($active ? "#0369a1" : "#ffffff")};
	color: ${({ $active }) => ($active ? "#ffffff" : "#374151")};

	span {
		display: inline-block;
		margin-left: 6px;
		font-variant-numeric: tabular-nums;
		opacity: 0.75;
	}

	&:hover {
		border-color: ${({ $active }) => ($active ? "#0369a1" : "#cbd5e1")};
	}
`;

const ChainTableWrap = styled.div`
	border: 1px solid #e5e7eb;
	border-radius: 14px;
	overflow-x: auto;
`;

const ChainTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;

	th {
		text-align: left;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: #6b7280;
		padding: 9px 12px;
		border-bottom: 1px solid #e5e7eb;
		white-space: nowrap;
	}

	td {
		padding: 10px 12px;
		border-bottom: 1px solid #f0f2f5;
		vertical-align: middle;
	}

	tr:last-child td {
		border-bottom: 0;
	}

	a {
		color: #0369a1;
		text-decoration: none;
	}

	a:hover {
		text-decoration: underline;
	}
`;

const ChainMissing = styled.span`
	color: #dc2626;
	font-weight: 600;
`;

const ChainDone = styled.span`
	color: #059669;
	font-weight: 600;
`;

const SmallButton = styled.button<{ $primary?: boolean }>`
	appearance: none;
	font-size: 12px;
	font-weight: 700;
	padding: 6px 12px;
	border-radius: 9px;
	cursor: pointer;
	border: 1px solid ${({ $primary }) => ($primary ? "#059669" : "#e5e7eb")};
	background: ${({ $primary }) => ($primary ? "#059669" : "#ffffff")};
	color: ${({ $primary }) => ($primary ? "#ffffff" : "#0369a1")};

	&:hover {
		background: ${({ $primary }) => ($primary ? "#047857" : "#f0f9ff")};
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
`;

const SectionHead = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 12px;
`;

const ChainNote = styled.p`
	margin: 0 0 14px;
	padding: 11px 13px;
	border: 1px solid #e4d9ff;
	border-radius: 12px;
	background: #f5f0ff;
	color: #4c1d95;
	font-size: 12.5px;
	line-height: 1.55;
`;

const ActionBar = styled.div`
	padding: 14px 20px;
	border-top: 1px solid #e5e7eb;
	background: #fcfdfe;
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
	flex-shrink: 0;
`;

const ActionHint = styled.div`
	color: #6b7280;
	font-size: 13px;
	line-height: 1.5;
	margin-right: auto;
	max-width: 46ch;
`;

const ActionButton = styled.button<{ $variant?: "deny" | "approve" }>`
	padding: 12px 18px;
	border-radius: 12px;
	border: 1px solid ${({ $variant }) => ($variant === "deny" ? "#dc2626" : "#059669")};
	background: ${({ $variant }) => ($variant === "deny" ? "#ffffff" : "#059669")};
	color: ${({ $variant }) => ($variant === "deny" ? "#dc2626" : "#ffffff")};
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: 0.2s ease;

	:hover {
		opacity: 0.9;
	}

	:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const PaginationWrap = styled.div`
	margin-top: 20px;
	padding: 0 20px 20px;
`;

type StatsState = {
	pending: number;
	awaitingSignature: number;
	expired: number;
	denied: number;
	total: number;
};

/**
 * Nhãn trạng thái NHÓM 1 — bản thiết kế anh Quân duyệt 27/08/2026 mục 4.3:
 * Draft · Need Approval · Awaiting Signature · Expired · Declined.
 *
 * Máy chủ tính sẵn và gửi kèm trong `trangThaiHoSo` — ở đây KHÔNG tính lại, vì hồ sơ
 * luồng cũ không mang dấu vết nào cho biết nó đã thành station hay chưa; chỉ máy chủ
 * tra `Partners` mới biết. Nhánh dưới chỉ để đỡ cho hồ sơ nào máy chủ chưa kịp gắn.
 */
/*
 * `Draft` CỐ Ý không có trong màn này (30/08/2026).
 *
 * Bản thiết kế 27/08 mục 4.2 định nghĩa Draft là **nháp của sale** — nhân viên gọi điện
 * rồi ngồi điền hộ nhưng chưa nộp. Rà mã 30/08: KHÔNG có đường nào tạo hồ sơ ở trạng
 * thái đó, form công khai nộp thẳng vào `Need Approval`, nút "sale lưu nháp" chưa tồn
 * tại. Để trong bộ lọc là để một lựa chọn không bao giờ ra kết quả, và nháp nội bộ
 * chưa nộp thì cũng không thuộc hàng đợi duyệt.
 *
 * Hằng số `FormStatus.Draft` vẫn giữ ở máy chủ — làm nút lưu nháp thì bật lên là chạy.
 */
function getStatusMeta(request?: StationRequestRecord | null) {
	const ma = request?.trangThaiHoSo?.ma;
	const nhan = request?.trangThaiHoSo?.nhan;

	if (ma && nhan) {
		switch (ma) {
			case "need_approval":
				return { label: nhan, color: "#D97706", background: "#FEF3C7" };
			case "awaiting_signature":
				return { label: nhan, color: "#0369a1", background: "#e0f2fe" };
			case "expired":
				/* Hết hạn ký là việc PHẢI làm gì đó (sale bấm gửi lại), không phải
				   trạng thái nghỉ — nên dùng màu cảnh báo chứ không xám. */
				return { label: nhan, color: "#b45309", background: "#ffedd5" };
			case "declined":
				return { label: nhan, color: "#DC2626", background: "#FEE2E2" };
			case "converted":
				return { label: nhan, color: "#059669", background: "#D1FAE5" };
			default:
				break;
		}
	}

	switch (request?.status) {
		case StationRequestStatus.Accepted:
			return { label: "Awaiting Signature", color: "#0369a1", background: "#e0f2fe" };
		case StationRequestStatus.Denied:
			return { label: "Declined", color: "#DC2626", background: "#FEE2E2" };
		default:
			return { label: "Need Approval", color: "#D97706", background: "#FEF3C7" };
	}
}

function getStationName(request?: StationRequestRecord | null) {
	const venue = resolveStationRequestVenueName(request);
	if (venue) return venue;
	if (request?.email) return request.email;
	return request?.id ? `Request #${request.id}` : "Unnamed station";
}

function getVerificationMethod(request?: StationRequestRecord | null) {
	return (
		request?.contractInfo?.verificationMethod ||
		((request?.contractInfo?.businessCertificateFiles || []).length ? "certificate" : "")
	);
}

function isCreateAccountRequest(request?: StationRequestRecord | null) {
	return getVerificationMethod(request) === "create_account";
}

function getVerificationLabel(request?: StationRequestRecord | null) {
	const verificationMethod = getVerificationMethod(request);
	if (verificationMethod === "owner_esign") return "Owner phone + email e-sign";
	if (verificationMethod === "certificate") return "Business certificate";
	if (verificationMethod === "create_account") return "Create account on submit";
	return "No verification";
}

function getAddress(request?: StationRequestRecord | null) {
	return request?.partnerInfo?.displayAddress || request?.partnerInfo?.address || "No address provided";
}

function getBusinessType(request?: StationRequestRecord | null) {
	const v = resolveStationRequestBusinessTypeDisplay(request);
	return v || "N/A";
}

function getRepresentativePhoneDisplay(request?: StationRequestRecord | null) {
	const phone = resolveStationRequestRepresentativePhone(request);
	if (phone) return phone;
	return isCreateAccountRequest(request) ? "Not provided" : "-";
}

function formatHourRange(range?: { from?: string; to?: string }, isAllDay?: boolean) {
	if (isAllDay) return "24 hours";
	if (!range?.from && !range?.to) return "Not provided";
	return `${range?.from || "--:--"} - ${range?.to || "--:--"}`;
}

function formatDate(value?: string) {
	if (!value) return "-";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("vi-VN");
}

function isValidUrl(value: string) {
	if (!value) return false;
	try {
		new URL(value);
		return true;
	} catch (error) {
		return false;
	}
}

function normalizeEmail(value?: string | null) {
	return String(value || "")
		.trim()
		.toLowerCase();
}

function normalizePhone(value?: string | null) {
	const trimmedValue = String(value || "").trim();
	if (!trimmedValue) return "";
	const hasLeadingPlus = trimmedValue.startsWith("+");
	const digitsOnly = trimmedValue.replace(/\D/g, "");
	return hasLeadingPlus ? `+${digitsOnly}` : digitsOnly;
}

function isValidPhone(value: string) {
	return /^[+]?[0-9]+$/.test(value);
}

type StationRequestEditState = {
	businessName: string;
	hotelName: string;
	businessType: string;
	/* Chức danh người liên hệ — Sheet cột "Admin duyệt hồ sơ" ghi Bắt buộc, nhưng màn
	   này trước đây không có ô nào ⇒ hồ sơ nào thiếu là Admin không có cách nào duyệt. */
	contactTitle: string;
	/* Ảnh khu gửi đồ. Cũng Bắt buộc theo Sheet; hồ sơ nộp qua form cũ không có tấm nào. */
	storageAreaImages: string[];
	address: string;
	mapLink: string;
	representativeName: string;
	ownerEmail: string;
	representativePhone: string;
	country: string;
	destinationId: string;
	cityName: string;
	openAllDay: boolean;
	staffedAllDay: boolean;
	openFrom: string;
	openTo: string;
	staffFrom: string;
	staffTo: string;
	/** Decimal degrees; saved on Form.partnerInfo for go-live (override map URL parsing when set) */
	lat: string;
	long: string;
};

function findDestinationById(destinations: StationRequestDestinationOption[], destinationId?: string | null) {
	return destinations.find((item) => item.id === destinationId) || null;
}

function createEditState(
	request: StationRequestRecord,
	destinations: StationRequestDestinationOption[]
): StationRequestEditState {
	const deskHours = resolveFrontDeskHoursRange(request?.partnerInfo);
	const staffedHours = resolveStaffedHoursRange(request?.partnerInfo);
	const selectedDestination = findDestinationById(destinations, request?.partnerInfo?.destinationId);

	return {
		businessName: resolveStationRequestBusinessName(request),
		hotelName: String(
			request?.partnerInfo?.hotelName || request?.partnerInfo?.name || request?.partnerInfo?.displayName || ""
		).trim(),
		businessType: resolveStationRequestBusinessTypeDisplay(request),
		contactTitle: String(request?.contractInfo?.contactTitle || "").trim(),
		storageAreaImages: (request?.partnerInfo?.storageAreaImages || []).filter(Boolean),
		address: String(request?.partnerInfo?.address || "").trim(),
		mapLink: String(request?.partnerInfo?.mapLink || "").trim(),
		representativeName: resolveStationRequestRepresentativeName(request),
		ownerEmail: normalizeEmail(request?.contractInfo?.ownerEmail || request?.email),
		representativePhone: resolveStationRequestRepresentativePhone(request),
		country: String(request?.partnerInfo?.country || selectedDestination?.country || "").trim(),
		destinationId: String(request?.partnerInfo?.destinationId || "").trim(),
		cityName: String(request?.partnerInfo?.city || selectedDestination?.name || "").trim(),
		openAllDay: Boolean(request?.partnerInfo?.openAllDay),
		staffedAllDay: Boolean(request?.partnerInfo?.staffedAllDay),
		openFrom: deskHours.from,
		openTo: deskHours.to,
		staffFrom: staffedHours.from,
		staffTo: staffedHours.to,
		lat:
			request?.partnerInfo?.lat !== undefined && request?.partnerInfo?.lat !== null
				? String(request.partnerInfo.lat)
				: "",
		long:
			request?.partnerInfo?.long !== undefined && request?.partnerInfo?.long !== null
				? String(request.partnerInfo.long)
				: "",
	};
}

function validateEditState(editState: StationRequestEditState, laChuoi = false) {
	const nextErrors: Record<string, string> = {};
	const normalizedPhone = normalizePhone(editState.representativePhone);

	// Trường PHÁP NHÂN/liên hệ — áp cho cả chủ chuỗi lẫn khách sạn đơn.
	if (!editState.hotelName.trim()) nextErrors.hotelName = "Required";
	if (!editState.representativeName.trim()) nextErrors.representativeName = "Required";
	if (!editState.businessType.trim()) nextErrors.businessType = "Required";
	if (!editState.ownerEmail.trim()) nextErrors.ownerEmail = "Required";
	else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editState.ownerEmail.trim())) nextErrors.ownerEmail = "Invalid email";
	if (!normalizedPhone) nextErrors.representativePhone = "Required";
	else if (!isValidPhone(normalizedPhone)) nextErrors.representativePhone = "Invalid phone";

	// Trường VỊ TRÍ — chủ chuỗi là pháp nhân không có địa điểm riêng, toạ độ/địa chỉ/
	// nước/thành phố/giờ nằm ở TỪNG ĐIỂM CON (modal Add location), nên BỎ required cho chuỗi.
	if (!laChuoi) {
		if (!editState.address.trim()) nextErrors.address = "Required";
		if (!editState.mapLink.trim()) nextErrors.mapLink = "Required";
		else if (!isValidUrl(editState.mapLink.trim())) nextErrors.mapLink = "Invalid URL";
		if (!editState.country.trim()) nextErrors.country = "Required";
		if (!editState.destinationId.trim() && !editState.cityName.trim()) nextErrors.destinationId = "Required";
		if (!editState.openAllDay) {
			if (!editState.openFrom.trim()) nextErrors.openFrom = "Required";
			if (!editState.openTo.trim()) nextErrors.openTo = "Required";
		}
		// Giờ trực gộp chung với "Opening Hours" (yêu cầu Quân 02/09) — không kiểm riêng nữa.

		const latT = editState.lat.trim();
		const longT = editState.long.trim();
		if (!latT || !longT) {
			if (!latT) nextErrors.lat = "Required for go-live";
			if (!longT) nextErrors.long = "Required for go-live";
		} else {
			const la = Number(latT);
			const lo = Number(longT);
			if (!Number.isFinite(la) || la < -90 || la > 90) {
				nextErrors.lat = "Latitude must be between -90 and 90.";
			}
			if (!Number.isFinite(lo) || lo < -180 || lo > 180) {
				nextErrors.long = "Longitude must be between -180 and 180.";
			}
		}
	}

	return nextErrors;
}

/** Saved partnerInfo must include finite lat/long before Go live (matches backend verify-golive). */
function hasGoLiveCoordinates(request: StationRequestRecord | null): boolean {
	const pi = request?.partnerInfo;
	if (!pi) return false;
	const lat = Number(pi.lat);
	const lng = Number(pi.long);
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
	if (lat < -90 || lat > 90) return false;
	if (lng < -180 || lng > 180) return false;
	return true;
}

function matchesKeyword(request: StationRequestRecord, keyword: string) {
	const pi = request.partnerInfo || {};
	const haystack = [
		getStationName(request),
		resolveStationRequestBusinessName(request),
		request.email,
		request.contractInfo?.ownerEmail,
		request.contractInfo?.ownerPhone,
		request.contractInfo?.ownerFullName,
		pi.representativePhone,
		pi.businessName,
		request.contractInfo?.businessLegalName,
		pi.country,
		pi.city,
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();

	return haystack.includes(keyword);
}

export default function StationRequestsDashboardPage() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const isDenied = isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin);
	const { page, doChangePage, filterKeyword, filterStates, doFilterKeyword, doFilterStates } =
		useCommonListFunctions();
	const { openAlertDialog } = useAlertDialog();

	const [requests, setRequests] = useState<StationRequestRecord[]>([]);
	const [count, setCount] = useState(0);
	const [selectedRequest, setSelectedRequest] = useState<StationRequestRecord | null>(null);
	const [stats, setStats] = useState<StatsState>({
		pending: 0,
		awaitingSignature: 0,
		expired: 0,
		denied: 0,
		total: 0,
	});
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [actingId, setActingId] = useState<number | null>(null);
	const [statsReloadKey, setStatsReloadKey] = useState(0);
	/** Lọc hàng đợi theo NHÓM trạng thái khi bấm ô chỉ số (need_approval/awaiting_signature/expired/declined; "" = tất cả). */
	const [cardFilter, setCardFilter] = useState<string>("");
	const [denyReason, setDenyReason] = useState("");
	const [denyDialogOpen, setDenyDialogOpen] = useState(false);
	const [denyDialogError, setDenyDialogError] = useState("");
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	/* Hộp thoại thêm/sửa một điểm của chuỗi. `chiSoDiem === null` là thêm mới. */
	/* Lọc theo loại hồ sơ: tất cả / một điểm / chuỗi. */
	const [locKind, setLocKind] = useState<"all" | "single" | "chain">("all");
	const [diemDialogOpen, setDiemDialogOpen] = useState(false);
	const [diemDangSua, setDiemDangSua] = useState<DiemChuoi>({ ...DIEM_RONG });
	const [chiSoDiem, setChiSoDiem] = useState<number | null>(null);
	const [diemLoi, setDiemLoi] = useState<Record<string, string>>({});
	const [diemDangLuu, setDiemDangLuu] = useState(false);
	const [dangTrichMaps, setDangTrichMaps] = useState(false);
	const [editDialogError, setEditDialogError] = useState("");
	const [editSaving, setEditSaving] = useState(false);
	const [dangTaiAnh, setDangTaiAnh] = useState(false);
	const [editErrors, setEditErrors] = useState<Record<string, string>>({});
	const [destinations, setDestinations] = useState<StationRequestDestinationOption[]>([]);
	const [editState, setEditState] = useState<StationRequestEditState | null>(null);
	const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
	const [previewImageLabel, setPreviewImageLabel] = useState("");
	const [geonameCityNames, setGeonameCityNames] = useState<string[]>([]);
	const [stationCityDebounced, setStationCityDebounced] = useState("");

	useEffect(() => {
		const t = window.setTimeout(() => setStationCityDebounced(editState?.cityName || ""), 400);
		return () => window.clearTimeout(t);
	}, [editState?.cityName]);

	useEffect(() => {
		if (!editState?.country) {
			setGeonameCityNames([]);
			return;
		}
		const countryInfo = COUNTRY_BY_NAME.get(editState.country);
		if (!countryInfo) {
			setGeonameCityNames([]);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const q = String(stationCityDebounced || "").trim();
				const rows =
					q.length >= 2
						? await searchGeonamePlacesForAdmin(q, { countryCode: countryInfo.code, limit: 300 })
						: await browseGeonamePlacesByCountry(countryInfo.code, 2000);
				if (cancelled) {
					return;
				}
				setGeonameCityNames(rows.map((r) => r.cityName).filter(Boolean));
			} catch {
				if (!cancelled) {
					setGeonameCityNames([]);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [editState?.country, stationCityDebounced]);

	const loadRequests = async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const parsedStatus = filterStates ? parseInt(filterStates, 10) : NaN;
			const response = await getStationRequests(
				page || 1,
				PER_PAGE,
				Number.isNaN(parsedStatus) ? null : (parsedStatus as StationRequestStatus),
				StationRequestType.Register,
				true
			);
			setRequests(response.docs || []);
			setCount(response.count || 0);
		} catch (err) {
			console.error("[StationRequests] Failed to load requests:", err);
			setLoadError("Failed to load station requests. Please try again.");
			setRequests([]);
			setCount(0);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Đếm theo NĂM trạng thái nhóm 1.
	 *
	 * Trước đây gọi bốn lần, mỗi lần đếm một `status` trong bảng. Cách đó không tách
	 * được `Awaiting Signature` / `Expired` / đã ký — cả ba đều là `status = 2`. Nay
	 * gọi MỘT lần rồi đếm trên trạng thái máy chủ đã tính. Hàng đợi thật có 71 hồ sơ
	 * (đo prod 30/08), lấy 300 là dư sức mà vẫn nhẹ hơn bốn lượt gọi cũ.
	 */
	const loadStats = async () => {
		try {
			const tatCa = await getStationRequests(1, 300, null, StationRequestType.Register, true);
			const ds = tatCa.docs || [];
			const dem = { need_approval: 0, awaiting_signature: 0, expired: 0, declined: 0 };
			let nhom1 = 0;
			ds.forEach((hoSo) => {
				if (hoSo.trangThaiHoSo?.nhom === 2) return;
				nhom1 += 1;
				const ma = hoSo.trangThaiHoSo?.ma;
				if (ma && ma in dem) dem[ma as keyof typeof dem] += 1;
			});
			setStats({
				pending: dem.need_approval,
				awaitingSignature: dem.awaiting_signature,
				expired: dem.expired,
				denied: dem.declined,
				/* Tổng là số hồ sơ CÒN trong nhóm 1 — hồ sơ đã ký thuộc thế giới station,
				   đếm vào đây là trộn lại hai nhóm. */
				total: nhom1,
			});
		} catch {
			setStats({ pending: 0, awaitingSignature: 0, expired: 0, denied: 0, total: 0 });
		}
	};

	useEffect(() => {
		loadRequests();
	}, [page, filterStates, statsReloadKey]);

	useEffect(() => {
		loadStats();
	}, [statsReloadKey]);

	useEffect(() => {
		let mounted = true;
		getStationRequestDestinations("")
			.then((response) => {
				if (!mounted) return;
				setDestinations(Array.isArray(response) ? response : []);
			})
			.catch(() => {
				if (!mounted) return;
				setDestinations([]);
			});
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (!selectedRequest) return;
		const nextSelected = requests.find((request) => request.id === selectedRequest.id) || null;
		setSelectedRequest(nextSelected);
	}, [requests]);

	const keyword = (filterKeyword || "").trim().toLowerCase();
	/* Điểm lẻ và chuỗi là hai loại hồ sơ hỏi những thứ khác nhau và xử lý khác nhau:
	   điểm lẻ duyệt là xong, chuỗi thì nhân viên còn phải gọi rồi dựng từng điểm.
	   Trộn chung một hàng đợi thì lúc nào cũng phải đọc từng dòng để đoán loại. */
	const filteredRequests = requests.filter((request) => {
		if (cardFilter && request.trangThaiHoSo?.ma !== cardFilter) return false;
		/* Ký xong thì hồ sơ KHÔNG còn trạng thái riêng — nó đóng lại thành giấy tờ lưu
		   trữ và trỏ sang station (bản thiết kế 27/08 mục 4.3). Giữ nó trong hàng đợi là
		   trộn lại hai nhóm: nhân viên thấy một dòng "đang chờ" cho điểm đã bán từ lâu. */
		if (request.trangThaiHoSo?.nhom === 2) return false;
		if (locKind === "single" && laHoSoChuoi(request)) return false;
		if (locKind === "chain" && !laHoSoChuoi(request)) return false;
		if (!keyword) return true;
		return matchesKeyword(request, keyword);
	});

	/* Đếm trên hồ sơ CÒN thuộc nhóm 1. Hồ sơ đã ký không nằm trong hàng đợi nữa, nên
	   đếm cả chúng là con số trên tab không khớp số dòng nhìn thấy. */
	const hoSoNhom1 = requests.filter((r) => r.trangThaiHoSo?.nhom !== 2);
	const soDiemLe = hoSoNhom1.filter((r) => !laHoSoChuoi(r)).length;
	const soChuoi = hoSoNhom1.filter((r) => laHoSoChuoi(r)).length;


	useEffect(() => {
		if (!filteredRequests.length) {
			setSelectedRequest(null);
			return;
		}

		if (!selectedRequest) {
			setSelectedRequest(filteredRequests[0]);
			return;
		}

		const stillVisible = filteredRequests.some((request) => request.id === selectedRequest.id);
		if (!stillVisible) {
			setSelectedRequest(filteredRequests[0]);
		}
	}, [filteredRequests, selectedRequest]);

	/**
	 * Cấp lại mật khẩu tạm cho hồ sơ ĐÃ DUYỆT (luồng ONBOARDING_V2).
	 *
	 * Mật khẩu tạm chỉ sống 7 ngày. Hết hạn là đối tác mất hẳn đường vào cổng — trước
	 * 29/08/2026 máy chủ đã có sẵn đường cấp lại nhưng màn này không có nút nào gọi tới,
	 * nên cách duy nhất là nhờ kỹ sư chạy tay.
	 */
	const guiLaiMatKhau = () => {
		if (!selectedRequest) return;
		openAlertDialog?.(
			AlertType.Confirm,
			`Cấp mật khẩu tạm MỚI cho ${selectedRequest.email || "hồ sơ này"} và gia hạn thêm 7 ngày? Mật khẩu cũ sẽ hết hiệu lực ngay.`,
			undefined,
			async () => {
				setActingId(selectedRequest.id);
				try {
					await guiLaiMatKhauTam(selectedRequest.id);
					openAlertDialog?.(
						AlertType.Success,
						"A new temporary password has been emailed to the station.",
					);
				} catch (err: any) {
					openAlertDialog?.(
						AlertType.Fail,
						err?.response?.data?.message || "Could not resend the temporary password.",
					);
				} finally {
					setActingId(null);
				}
			},
			undefined,
			{
				title: "Resend temporary password",
				declineText: "Cancel",
				acceptText: "Send",
			},
		);
	};

	const runAction = (mode: "approve" | "deny") => {
		if (!selectedRequest) return;

		const isApprove = mode === "approve";
		// Cổng legacy "station-portal legal" đã bỏ: luồng 3 bước ký SAU khi duyệt, nên
		// không thể hoàn tất legal trước duyệt (xem cùng lý do ở nút Approve).
		if (isApprove && !hasGoLiveCoordinates(selectedRequest) && !laHoSoChuoi(selectedRequest)) {
			openAlertDialog?.(
				AlertType.Fail,
				"Enter valid latitude and longitude in Edit details and save before Go live."
			);
			return;
		}
		if (!isApprove) {
			setDenyReason("");
			setDenyDialogError("");
			setDenyDialogOpen(true);
			return;
		}
		openAlertDialog?.(
			AlertType.Confirm,
			isApprove
				? `Duyệt sẽ tạo tài khoản station cho ${selectedRequest.email || "request này"}. Tiếp tục?`
				: `Từ chối station request của ${selectedRequest.email || "request này"}?`,
			undefined,
			async () => {
				setActingId(selectedRequest.id);
				try {
					await verifyStationGoLive(selectedRequest.id);
					setStatsReloadKey((prev) => prev + 1);
					openAlertDialog?.(
						AlertType.Success,
						isApprove ? "Station request approved successfully." : "Station request denied successfully."
					);
				} catch (err: any) {
					openAlertDialog?.(AlertType.Fail, err?.response?.data?.message || "Unable to update this request.");
				} finally {
					setActingId(null);
				}
			},
			undefined,
			{
				title: isApprove ? "Approve Station Request" : "Deny Station Request",
				declineText: "Cancel",
				acceptText: isApprove ? "Approve" : "Deny",
			}
		);
	};

	const handleConfirmDeny = async () => {
		if (!selectedRequest) return;
		const normalizedReason = denyReason.trim();

		if (!normalizedReason) {
			setDenyDialogError("Please enter a deny reason before rejecting this request.");
			return;
		}

		setActingId(selectedRequest.id);
		setDenyDialogError("");
		try {
			await denyStationRequest(selectedRequest.id, normalizedReason);
			setDenyDialogOpen(false);
			setDenyReason("");
			setStatsReloadKey((prev) => prev + 1);
			openAlertDialog?.(AlertType.Success, "Station request denied successfully.");
		} catch (err: any) {
			setDenyDialogError(err?.response?.data?.message || "Unable to update this request.");
		} finally {
			setActingId(null);
		}
	};

	const openEditDialog = () => {
		if (!selectedRequest) return;
		setEditState(createEditState(selectedRequest, destinations));
		setEditErrors({});
		setEditDialogError("");
		setEditDialogOpen(true);
	};

	/* ── Điểm của chuỗi ───────────────────────────────────────────────────────
	   Đội WhaleLO tự thêm từng điểm sau khi gọi cho chủ chuỗi. Lưu vào
	   `extenalInfo.multiLocation.locations[]` qua chính `PUT /forms/:id` đang chạy. */
	const moThemDiem = () => {
		setDiemDangSua({ ...DIEM_RONG });
		setChiSoDiem(null);
		setDiemLoi({});
		setDiemDialogOpen(true);
	};

	const moSuaDiem = (chiSo: number) => {
		if (!selectedRequest) return;
		const ds = danhSachDiem(selectedRequest);
		setDiemDangSua({ ...DIEM_RONG, ...(ds[chiSo] || {}) });
		setChiSoDiem(chiSo);
		setDiemLoi({});
		setDiemDialogOpen(true);
	};

	// Backend trả `canhBao` dưới dạng MÃ nội bộ (vd CHI_DOC_DUOC_TU_LINK); đổi sang câu
	// tiếng người cho admin hiểu, đừng để lọt mã thô ra toast.
	const moTaCanhBaoMaps = (ma?: string | null): string => {
		switch (ma) {
			case "CHI_DOC_DUOC_TU_LINK":
				return "Chỉ lấy được tên và toạ độ từ link — chưa lấy được số điện thoại/địa chỉ đầy đủ. Hãy kiểm tra và điền nốt tay.";
			case "SAI_DIA_DIEM_BO_DIA_CHI":
				return "Link có thể trỏ nhầm địa điểm nên đã bỏ phần địa chỉ cho an toàn. Kiểm tra lại thật kỹ.";
			case "KHONG_DOC_DUOC":
				return "Không đọc được thông tin từ link này.";
			case "LINK_KHONG_HOP_LE":
				return "Link Google Maps không hợp lệ.";
			case "KHONG_KET_NOI_DUOC":
				return "Không kết nối được tới Google Maps. Thử lại sau ít phút.";
			default:
				return ma ? `Lưu ý: ${ma}` : "";
		}
	};

	// Dán link Google Maps vào ô "Google Maps link" của điểm rồi bấm "Trích từ Maps" ->
	// tự điền Location name / Address / Coordinates / Phone (và Country/City khi backend
	// trả). Admin rà lại trước khi lưu.
	const trichTuMaps = async () => {
		const link = String(diemDangSua.maps || "").trim();
		if (!link) {
			setDiemLoi((prev) => ({ ...prev, maps: "Dán link Google Maps trước khi trích." }));
			return;
		}
		setDangTrichMaps(true);
		try {
			const data = await giaiLinkGoogleMapsChoDiem(link);
			const coToaDo = data?.lat != null && data?.long != null && data?.lat !== "" && data?.long !== "";
			if (!data || (!data.ten && !data.diaChi && !coToaDo)) {
				openAlertDialog?.(
					AlertType.Fail,
					data?.canhBao ? moTaCanhBaoMaps(data.canhBao) : "Không đọc được thông tin từ link này.",
				);
				return;
			}
			setDiemDangSua((prev) => ({
				...prev,
				ten: data.ten || prev.ten,
				diaChi: data.diaChi || prev.diaChi,
				toaDo: coToaDo ? `${data.lat}, ${data.long}` : prev.toaDo,
				dienThoai: data.soDienThoai || prev.dienThoai,
				nuoc: data.nuoc || prev.nuoc,
				thanhPho: data.thanhPho || prev.thanhPho,
				maps: data.urlDayDu || prev.maps,
			}));
			setDiemLoi({});
			openAlertDialog?.(
				AlertType.Success,
				data.canhBao
					? `Đã trích xong. ${moTaCanhBaoMaps(data.canhBao)}`
					: "Đã trích thông tin từ Maps. Vui lòng rà lại trước khi lưu.",
			);
		} catch (err) {
			openAlertDialog?.(AlertType.Fail, "Lỗi khi gọi trích từ Google Maps. Thử lại.");
		} finally {
			setDangTrichMaps(false);
		}
	};

	const luuDiem = async () => {
		if (!selectedRequest) return;
		const loi: Record<string, string> = {};
		O_DIEM.forEach((o) => {
			// Field đánh dấu tuyChon (VD "Login email (optional)") KHÔNG bắt buộc.
			if (!o.tuyChon && !String(diemDangSua[o.khoa] || "").trim()) {
				loi[o.khoa] =
					o.khoa === "nuoc"
						? "Country is required — the station rate comes from it."
						: `${o.nhan} is required.`;
			}
		});
		if (Object.keys(loi).length) {
			setDiemLoi(loi);
			return;
		}

		const ds = danhSachDiem(selectedRequest);
		const dsMoi = chiSoDiem === null ? [...ds, diemDangSua] : ds.map((d, i) => (i === chiSoDiem ? diemDangSua : d));

		setDiemDangLuu(true);
		try {
			await luuDiemChuoi(selectedRequest.id, dsMoi as unknown as Record<string, string>[]);
			setDiemDialogOpen(false);
			setStatsReloadKey((prev) => prev + 1);
			openAlertDialog?.(
				AlertType.Success,
				chiSoDiem === null ? "Location added." : "Location updated.",
			);
		} catch (err: any) {
			setDiemLoi({
				__chung: err?.response?.data?.message || "Could not save this location.",
			});
		} finally {
			setDiemDangLuu(false);
		}
	};

	const updateEditField = (field: keyof StationRequestEditState, value: string | boolean) => {
		setEditState((prev) => {
			if (!prev) return prev;
			const nextState = {
				...prev,
				[field]: value,
			} as StationRequestEditState;
			if (field === "country") {
				nextState.destinationId = "";
				nextState.cityName = "";
			}
			return nextState;
		});
		setEditErrors((prev) => ({
			...prev,
			[field]: "",
			...(field === "country" ? { destinationId: "" } : {}),
		}));
		setEditDialogError("");
	};

	/**
	 * Admin tải ảnh khu gửi đồ hộ khách sạn.
	 *
	 * Cần vì từ 30/08 hồ sơ phải ĐỦ mới bấm Approve được, mà ảnh là mục bắt buộc theo
	 * Sheet. 11/14 hồ sơ đang chờ trên production không có tấm nào — nộp qua form cũ,
	 * hồi đó form chưa thu ảnh. Không có chỗ tải thì chúng nằm đó mãi.
	 */
	const taiAnhKhuGuiDo = async (files: FileList | null) => {
		if (!files || !files.length) return;
		setDangTaiAnh(true);
		setEditDialogError("");
		try {
			const duongDan = await uploadStationRequestFiles(Array.from(files));
			setEditState((prev) =>
				prev ? { ...prev, storageAreaImages: [...prev.storageAreaImages, ...duongDan] } : prev
			);
		} catch (err: any) {
			setEditDialogError(err?.response?.data?.message || "Unable to upload the photos.");
		} finally {
			setDangTaiAnh(false);
		}
	};

	const xoaAnhKhuGuiDo = (duongDan: string) => {
		setEditState((prev) =>
			prev ? { ...prev, storageAreaImages: prev.storageAreaImages.filter((x) => x !== duongDan) } : prev
		);
	};

	const handleSaveEdit = async () => {
		if (!selectedRequest || !editState) return;

		const nextErrors = validateEditState(editState, laHoSoChuoi(selectedRequest));
		setEditErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			return;
		}

		const destination = findDestinationById(destinations, editState.destinationId);
		const cityName = destination?.name || editState.cityName.trim();
		if (!cityName) {
			setEditErrors((prev) => ({
				...prev,
				destinationId: "City is required",
			}));
			return;
		}

		const latRaw = editState.lat.trim();
		const longRaw = editState.long.trim();
		const la = Number(latRaw);
		const lo = Number(longRaw);
		const partnerInfoPayload: Record<string, unknown> = {
			...(selectedRequest.partnerInfo || {}),
			businessName: editState.businessName.trim(),
			hotelName: editState.hotelName.trim(),
			representativeName: editState.representativeName.trim(),
			representativePhone: normalizePhone(editState.representativePhone),
			address: editState.address.trim(),
			mapLink: editState.mapLink.trim(),
			businessType: editState.businessType.trim(),
			storageAreaImages: editState.storageAreaImages,
			country: editState.country.trim(),
			city: cityName,
			destinationId: editState.destinationId || "",
			openAllDay: editState.openAllDay,
			// Một khung "Opening Hours" duy nhất: giờ trực = giờ mở cửa.
			staffedAllDay: editState.openAllDay,
			openHours: editState.openAllDay
				? null
				: { from: editState.openFrom.trim(), to: editState.openTo.trim() },
			frontDeskHours: editState.openAllDay
				? null
				: { from: editState.openFrom.trim(), to: editState.openTo.trim() },
			staffedHours: editState.openAllDay
				? null
				: { from: editState.openFrom.trim(), to: editState.openTo.trim() },
			lat: la,
			long: lo,
		};

		const payload = {
			email: normalizeEmail(editState.ownerEmail),
			partnerInfo: partnerInfoPayload,
			contractInfo: {
				...(selectedRequest.contractInfo || {}),
				ownerFullName: editState.representativeName.trim(),
				ownerEmail: normalizeEmail(editState.ownerEmail),
				ownerPhone: normalizePhone(editState.representativePhone),
				contactTitle: editState.contactTitle.trim(),
			},
			extenalInfo: selectedRequest.extenalInfo || {},
		};

		setEditSaving(true);
		setEditDialogError("");
		try {
			await updateStationRequest(selectedRequest.id, payload);
			setEditDialogOpen(false);
			setStatsReloadKey((prev) => prev + 1);
			openAlertDialog?.(AlertType.Success, "Station request updated successfully.");
		} catch (err: any) {
			setEditDialogError(err?.response?.data?.message || "Unable to update this request.");
		} finally {
			setEditSaving(false);
		}
	};

	const openImagePreview = (url: string, label: string) => {
		setPreviewImageUrl(url);
		setPreviewImageLabel(label);
	};

	if (isDenied) {
		return (
			<MyBookingWrapper>
				<div style={{ textAlign: "center", padding: "50px" }}>
					<h2>Access Denied</h2>
					<p>You don't have permission to access this page.</p>
				</div>
			</MyBookingWrapper>
		);
	}

	const selectedImages = (selectedRequest?.partnerInfo?.storageAreaImages || []).filter(Boolean);
	const selectedCertificates = (selectedRequest?.contractInfo?.businessCertificateFiles || []).filter(Boolean);
	// Only http(s) map links become clickable; anything else is shown as plain text.
	const safeMapLink = safeExternalHref(selectedRequest?.partnerInfo?.mapLink);
	const statusMeta = getStatusMeta(selectedRequest);
	const selectedLegalComplete = isStationPortalLegalVerificationCompleted(selectedRequest);
	/* Chuỗi chỉ duyệt được khi đã thêm điểm và mọi điểm đủ thông tin — mỗi điểm rồi
	   thành một station thật. Hồ sơ một điểm luôn trả `san: true`. */
	const chainReady = laHoSoChuoi(selectedRequest)
		? chuoiSanSangDuyet(selectedRequest)
		: { san: true, lyDo: "" };
	const selectedHasGoLiveCoords = hasGoLiveCoordinates(selectedRequest);
	// Chủ chuỗi là pháp nhân KHÔNG có địa điểm riêng -> không đòi toạ độ ở hồ sơ chủ
	// (toạ độ nằm ở từng điểm con). Backend cũng đã bỏ chặn toạ độ cho chuỗi.
	const coordsOkForGoLive = selectedHasGoLiveCoords || laHoSoChuoi(selectedRequest);
	const selectedVerificationMethod = getVerificationMethod(selectedRequest);
	const isCreateAccount = isCreateAccountRequest(selectedRequest);
	const selectedDestination = findDestinationById(destinations, selectedRequest?.partnerInfo?.destinationId);
	const accountAccessLabel =
		selectedRequest?.status === StationRequestStatus.Accepted
			? "Station activated"
			: selectedRequest?.status === StationRequestStatus.Denied
			? "Request denied, waiting for resubmission"
			: "User account created, waiting for admin approval";
	const countryOptions = React.useMemo(() => {
		const byKey = new Map<string, string>();
		[
			...destinations.map((item) => String(item?.country || "").trim()),
			...COUNTRIES.map((country) => String(country.name || "").trim()),
		].forEach((countryRaw) => {
			const trimmed = String(countryRaw || "").trim();
			if (!trimmed) return;
			const key = normalizeCountryKey(trimmed);
			if (!key || byKey.has(key)) return;
			byKey.set(key, toCanonicalCountryName(trimmed) || trimmed);
		});
		return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
	}, [destinations]);
	const cityOptions = useMemo(() => {
		if (!editState?.country) return [];
		const destNames = new Set(
			destinations
				.filter((item) => normalizeCountryKey(item.country || "") === normalizeCountryKey(editState.country))
				.map((d) => d.name)
		);
		const all = new Set([...Array.from(destNames), ...geonameCityNames]);
		return Array.from(all).sort((a, b) => a.localeCompare(b));
	}, [destinations, editState?.country, geonameCityNames]);

	return (
		<PageWrap>
			<PageInner>
				<Hero>
					<div>
						<HeroTitle>Station Requests</HeroTitle>
						<HeroSubtitle>
							Quick review board for partner registration requests waiting for admin action.
						</HeroSubtitle>
					</div>
					<HeroMeta>
						<HeroMetaLabel>Queue</HeroMetaLabel>
						<HeroMetaValue>{count}</HeroMetaValue>
						<HeroMetaText>
							{filterStates === "1" ? "Pending requests in this view" : "Current server-side results"}
						</HeroMetaText>
					</HeroMeta>
				</Hero>

				{/*
				  HAI THẾ GIỚI (anh Quân chốt 28/08/2026): hồ sơ sống ở màn NÀY, station đã
				  là điểm bán sống ở Station Management. Thẻ cũ ghi "Live" nhưng lại đếm hồ
				  sơ đã duyệt — mượn chữ của thế giới bên kia cho một con số của bên này,
				  nên đọc xong tưởng có 12 điểm đang bán. Ba thẻ dưới đây đều là trạng thái
				  của HỒ SƠ, không có cái nào nói về điểm bán.
				*/}
				<StatsGrid>
					<StatCard
						$clickable
						$active={cardFilter === "need_approval"}
						onClick={() =>
							setCardFilter((v) => (v === "need_approval" ? "" : "need_approval"))
						}>
						<StatLabel>Need Approval</StatLabel>
						<StatValue>{stats.pending}</StatValue>
					</StatCard>
					<StatCard
						$clickable
						$active={cardFilter === "awaiting_signature"}
						onClick={() =>
							setCardFilter((v) =>
								v === "awaiting_signature" ? "" : "awaiting_signature",
							)
						}>
						<StatLabel>Awaiting Signature</StatLabel>
						<StatValue>{stats.awaitingSignature}</StatValue>
					</StatCard>
					<StatCard
						$clickable
						$active={cardFilter === "expired"}
						onClick={() => setCardFilter((v) => (v === "expired" ? "" : "expired"))}>
						<StatLabel>Expired</StatLabel>
						<StatValue>{stats.expired}</StatValue>
					</StatCard>
					<StatCard
						$clickable
						$active={cardFilter === "declined"}
						onClick={() => setCardFilter((v) => (v === "declined" ? "" : "declined"))}>
						<StatLabel>Declined</StatLabel>
						<StatValue>{stats.denied}</StatValue>
					</StatCard>
					<StatCard
						$clickable
						$active={cardFilter === ""}
						onClick={() => setCardFilter("")}>
						<StatLabel>Total</StatLabel>
						<StatValue>{stats.total}</StatValue>
					</StatCard>
				</StatsGrid>

				<MainGrid>
					<Panel>
						<PanelHeader style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
							<div style={{ flex: 1 }}>
								<PanelTitle>Queue</PanelTitle>
								<PanelSubtitle>Inbox [{filteredRequests.length} visible]</PanelSubtitle>
							</div>
							<Button
								size="small"
								variant="outlined"
								disabled={loading}
								onClick={() => loadRequests()}>
								Refresh
							</Button>
						</PanelHeader>
						{/* Tách hai loại hồ sơ ngay ở đầu hàng đợi. */}
						<KindTabs role="tablist" aria-label="Request type">
							{([
								["all", "All", hoSoNhom1.length],
								["single", "Single", soDiemLe],
								["chain", "Chain", soChuoi],
							] as const).map(([ma, nhan, dem]) => (
								<KindTab
									key={ma}
									type="button"
									role="tab"
									aria-selected={locKind === ma}
									$active={locKind === ma}
									onClick={() => setLocKind(ma)}>
									{nhan} <span>{dem}</span>
								</KindTab>
							))}
						</KindTabs>
						<FilterWrap>
							<SearchPanel
								placeholder="Search by station name or email"
								filterKeyword={filterKeyword}
								doFilterKeyword={doFilterKeyword}
							/>
							<FilterSelect
								value={filterStates || ""}
								onChange={(event) => doFilterStates?.(event.target.value || null)}>
								<option value="">All statuses</option>
								<option value="1">Need Approval</option>
								<option value="2">Awaiting Signature</option>
								<option value="3">Declined</option>
							</FilterSelect>
						</FilterWrap>
						<ListBody>
							{loading ? (
								<Placeholder>Loading station requests...</Placeholder>
							) : loadError ? (
								<Placeholder style={{ color: "#dc2626" }}>
									{loadError}
									<br />
									<Button
										size="small"
										variant="outlined"
										style={{ marginTop: 12 }}
										onClick={() => loadRequests()}>
										Retry
									</Button>
								</Placeholder>
							) : filteredRequests.length ? (
								<RequestList>
									{filteredRequests.map((request) => {
										const requestStatus = getStatusMeta(request);
										const legalComplete = isStationPortalLegalVerificationCompleted(request);
										const biz = resolveStationRequestBusinessName(request);
										const repPhone = resolveStationRequestRepresentativePhone(request);
										return (
											<RequestCard
												key={request.id}
												type="button"
												$selected={selectedRequest?.id === request.id}
												onClick={() => setSelectedRequest(request)}>
												<RequestCardTop>
													<div>
														<RequestTitle>{getStationName(request)}</RequestTitle>
														<RequestSub>{request.email || "-"}</RequestSub>
														{biz ? (
															<RequestSub style={{ opacity: 0.88 }}>
																Business: {biz}
															</RequestSub>
														) : null}
													</div>
													<StatusBadge
														color={requestStatus.color}
														background={requestStatus.background}>
														{requestStatus.label}
													</StatusBadge>
												</RequestCardTop>
												<RequestMeta>
													<LegalStatusChip $complete={legalComplete}>
														{legalComplete ? "Legal ✓" : "Legal pending"}
													</LegalStatusChip>
													<MetaChip>{getVerificationLabel(request)}</MetaChip>
													{repPhone ? <MetaChip>{repPhone}</MetaChip> : null}
												</RequestMeta>
												<RequestTime>{formatDate(request.createdAt)}</RequestTime>
											</RequestCard>
										);
									})}
								</RequestList>
							) : (
								<Placeholder>No station requests found for this view.</Placeholder>
							)}
						</ListBody>
						{count > PER_PAGE && (
							<PaginationWrap>
								<NumberPaginationBox
									page={page || 1}
									count={count}
									per={PER_PAGE}
									onChange={(value) => doChangePage?.(value)}
								/>
							</PaginationWrap>
						)}
					</Panel>

					<DetailPanel>
						{!selectedRequest ? (
							<Placeholder>Chọn một request để xem chi tiết.</Placeholder>
						) : (
							<>
							<DetailBody>
								<DetailTop>
									<div>
										<DetailTitle>{getStationName(selectedRequest)}</DetailTitle>
										<DetailMeta>
											#{selectedRequest.id} ·{" "}
											{formatDate(
												selectedRequest.extenalInfo?.submittedAt || selectedRequest.createdAt
											)}
										</DetailMeta>
										<DetailLegalHint>
											<LegalStatusChip $complete={selectedLegalComplete}>
												{selectedLegalComplete ? "Legal ✓ · Ready for review" : "Legal pending"}
											</LegalStatusChip>
										</DetailLegalHint>
									</div>
									<StatusBadge
										color={statusMeta.color}
										background={statusMeta.background}>
										{statusMeta.label}
									</StatusBadge>
								</DetailTop>

								<InfoGrid>
									<InfoCard>
										<InfoLabel>Business name</InfoLabel>
										<InfoValue>
											{resolveStationRequestBusinessName(selectedRequest) || "—"}
										</InfoValue>
									</InfoCard>
									<InfoCard>
										<InfoLabel>Hotel / Station name</InfoLabel>
										<InfoValue>{resolveStationRequestVenueName(selectedRequest) || "—"}</InfoValue>
									</InfoCard>
									{/* Bảng "Vòng đời Station" (29/08/2026): hồ sơ có thể khai HAI email —
									    Email 1 nhận thư duyệt và xác nhận thay đổi quan trọng, Email 2 dùng để
									    đăng nhập và nhận booking. Không khai Email 2 thì cả hai là một. */}
									<InfoCard>
										<InfoLabel>Email 1 — approvals</InfoLabel>
										<InfoValue>
											{selectedRequest.contractInfo?.ownerEmail || selectedRequest.email || "-"}
										</InfoValue>
									</InfoCard>
									{/* Chủ chuỗi KHÔNG nhận đơn (điểm con nhận) -> ẩn Email 2 cho hồ sơ chuỗi. */}
									{!laHoSoChuoi(selectedRequest) && (
										<InfoCard>
											<InfoLabel>Email 2 — login &amp; bookings</InfoLabel>
											<InfoValue>
												{selectedRequest.email &&
												selectedRequest.email !== selectedRequest.contractInfo?.ownerEmail
													? selectedRequest.email
													: "Same as Email 1"}
											</InfoValue>
										</InfoCard>
									)}
									<InfoCard>
										<InfoLabel>Representative name</InfoLabel>
										<InfoValue>
											{resolveStationRequestRepresentativeName(selectedRequest) || "—"}
										</InfoValue>
									</InfoCard>
									<InfoCard>
										<InfoLabel>Position title</InfoLabel>
										<InfoValue>
											{selectedRequest.contractInfo?.contactTitle ||
												selectedRequest.partnerInfo?.representativeTitle ||
												selectedRequest.contractInfo?.ownerTitle ||
												"—"}
										</InfoValue>
									</InfoCard>
									<InfoCard>
										<InfoLabel>Registration type</InfoLabel>
										<InfoValue>
											{laHoSoChuoi(selectedRequest)
												? `Chain — ${
														selectedRequest.extenalInfo?.multiLocation?.chainName || "unnamed"
												  } (${soDiemUocTinh(selectedRequest) || "?"} locations)`
												: "Single station"}
										</InfoValue>
									</InfoCard>
									<InfoCard>
										<InfoLabel>Representative phone</InfoLabel>
										<InfoValue>{getRepresentativePhoneDisplay(selectedRequest)}</InfoValue>
									</InfoCard>
								</InfoGrid>

								{(() => {
									const portalLegal = getStationPortalLegalVerificationPanelEn(selectedRequest);
									return (
										<Section>
											<SectionTitle>Station portal — legal &amp; identification</SectionTitle>
											<KeyValueList>
												<KeyValueItem>
													<KeyLabel>Completion status</KeyLabel>
													<KeyValue>{portalLegal.statusLine}</KeyValue>
												</KeyValueItem>
												{portalLegal.completed &&
													portalLegal.rows.map((row) => (
														<KeyValueItem key={row.label}>
															<KeyLabel>{row.label}</KeyLabel>
															<KeyValue>{row.value}</KeyValue>
														</KeyValueItem>
													))}
											</KeyValueList>
										</Section>
									);
								})()}

								{/* Hồ sơ CHUỖI: khối riêng, do đội WhaleLO tự dựng từng điểm sau khi gọi
								    cho chủ chuỗi. Hồ sơ một điểm không thấy khối này. */}
								{laHoSoChuoi(selectedRequest) ? (
									<Section>
										<SectionHead>
											<SectionTitle style={{ margin: 0 }}>
												Locations ({danhSachDiem(selectedRequest).length} added
												{soDiemUocTinh(selectedRequest)
													? ` of ~${soDiemUocTinh(selectedRequest)} estimated`
													: ""}
												)
											</SectionTitle>
											<PermissionGate permission="stations.manage">
												<SmallButton
													type="button"
													$primary
													style={{ marginLeft: "auto" }}
													onClick={moThemDiem}>
													+ Add location
												</SmallButton>
											</PermissionGate>
										</SectionHead>

										<ChainNote>
											{tenChuoi(selectedRequest) ? `${tenChuoi(selectedRequest)} — ` : ""}
											chain application. The group only gives a name and a rough number of
											locations; our team calls them and adds each location here. Every
											location becomes a real station, so it needs the same details as a
											single-hotel application.
										</ChainNote>

										{danhSachDiem(selectedRequest).length ? (
											<ChainTableWrap>
												<ChainTable>
													<thead>
														<tr>
															<th>#</th>
															<th>Location</th>
															<th>City</th>
															<th>Maps</th>
															<th>Status</th>
															<th />
														</tr>
													</thead>
													<tbody>
														{danhSachDiem(selectedRequest).map((diem, index) => {
															const thieu = diemConThieu(diem);
															// Chỉ link http(s) mới bấm được (audit 2026-10-03 P1-9); giá trị lạ hiện dạng chữ.
															const mapsHref = safeExternalHref(diem.maps);
															return (
																<tr key={`${diem.ten}-${index}`}>
																	<td style={{ color: "#6b7280" }}>{index + 1}</td>
																	<td>{diem.ten || "—"}</td>
																	<td>{diem.thanhPho || "—"}</td>
																	<td>
																		{mapsHref ? (
																			<a
																				href={mapsHref}
																				target="_blank"
																				rel="noreferrer">
																				Open
																			</a>
																		) : (
																			<ChainMissing>—</ChainMissing>
																		)}
																	</td>
																	<td>
																		{thieu.length ? (
																			<ChainMissing>
																				Missing {thieu.join(", ")}
																			</ChainMissing>
																		) : (
																			<ChainDone>Complete</ChainDone>
																		)}
																	</td>
																	<td>
																		<PermissionGate permission="stations.manage">
																			<SmallButton
																				type="button"
																				onClick={() => moSuaDiem(index)}>
																				Edit
																			</SmallButton>
																		</PermissionGate>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</ChainTable>
											</ChainTableWrap>
										) : (
											<KeyValue>
												No location added yet. Call the group, then add each one here.
											</KeyValue>
										)}
									</Section>
								) : null}

								<Section>
									<SectionTitle>Profile</SectionTitle>
									<KeyValueList>
										<KeyValueItem>
											<KeyLabel>Address</KeyLabel>
											<KeyValue>{getAddress(selectedRequest)}</KeyValue>
										</KeyValueItem>
										<KeyValueItem>
											<KeyLabel>Map</KeyLabel>
											<KeyValue>
												{safeMapLink ? (
													<LinkValue
														href={safeMapLink}
														target="_blank"
														rel="noreferrer">
														Open map link
													</LinkValue>
												) : (
													selectedRequest.partnerInfo?.mapLink || "-"
												)}
											</KeyValue>
										</KeyValueItem>
										<KeyValueItem>
											<KeyLabel>Business Type</KeyLabel>
											<KeyValue>{getBusinessType(selectedRequest)}</KeyValue>
										</KeyValueItem>
										<KeyValueItem>
											<KeyLabel>Country</KeyLabel>
											<KeyValue>
												{selectedRequest.partnerInfo?.country ||
													selectedDestination?.country ||
													"-"}
											</KeyValue>
										</KeyValueItem>
										<KeyValueItem>
											<KeyLabel>City</KeyLabel>
											<KeyValue>
												{selectedRequest.partnerInfo?.city || selectedDestination?.name || "-"}
											</KeyValue>
										</KeyValueItem>
										<KeyValueItem>
											<KeyLabel>Opening Hours</KeyLabel>
											<KeyValue>
												{formatHourRange(
													resolveFrontDeskHoursRange(selectedRequest.partnerInfo),
													Boolean(selectedRequest.partnerInfo?.openAllDay)
												)}
											</KeyValue>
										</KeyValueItem>
									</KeyValueList>

									<SectionTitle style={{ marginTop: "18px" }}>Photos of Storage</SectionTitle>
									{selectedImages.length ? (
										<ImageGrid>
											{selectedImages.map((image: string, index: number) => (
												<ImageItem
													key={`${image}-${index}`}
													type="button"
													onClick={() =>
														openImagePreview(image, `Storage area ${index + 1}`)
													}>
													<Image
														src={image}
														alt={`Storage area ${index + 1}`}
													/>
												</ImageItem>
											))}
										</ImageGrid>
									) : (
										<KeyValue>No images submitted.</KeyValue>
									)}
								</Section>

								{/*
								  Khối "Account Setup / Contract" đã bỏ (29/08/2026).
								  Đo 71 hồ sơ đăng ký trên production từ 01/01/2026: Verification Method,
								  Account Flow, Station Access, E-sign Accepted, Signed At và Business
								  certificate đều là 0 hồ sơ có dữ liệu — chúng thuộc luồng ký điện tử đã
								  ngừng dùng. Email và tên người đại diện thì đã in ở lưới trên, in lại ở
								  đây là hai lần cùng một thứ.
								*/}
								{selectedRequest.denyReason ? (
									<Section>
										<SectionTitle>Deny Reason</SectionTitle>
										<KeyValue>{selectedRequest.denyReason}</KeyValue>
									</Section>
								) : null}

							</DetailBody>
						<ActionBar>
								{selectedRequest.status === StationRequestStatus.Pending ? (
									<>
										{/* Một dòng duy nhất: hoặc nói đang vướng gì, hoặc nói bấm duyệt thì
										    chuyện gì xảy ra. Từ 28/08/2026 duyệt KHÔNG đưa điểm lên bán nữa —
										    nó tạo tài khoản và gửi mật khẩu tạm, điểm lên bán khi trạm ký. */}
										<ActionHint>
											{!coordsOkForGoLive
												? "Blocked: save latitude and longitude in Edit details first."
												: !chainReady.san
												? `Blocked: ${chainReady.lyDo}`
												: `Approving creates the account and emails a temporary password to ${
														selectedRequest.contractInfo?.ownerEmail ||
														selectedRequest.email ||
														"the applicant"
												  }. ${
														laHoSoChuoi(selectedRequest)
															? "Locations go live one by one after the owner signs."
															: "The location goes live once the station signs."
												  }`}
										</ActionHint>
										<PermissionGate permission="stations.manage">
											<ActionWrap>
												<ActionButton
													type="button"
													$variant="deny"
													style={{ borderColor: "#0369a1", color: "#0369a1" }}
													disabled={actingId === selectedRequest.id}
													onClick={openEditDialog}>
													Edit details
												</ActionButton>
												<ActionButton
													type="button"
													$variant="deny"
													disabled={actingId === selectedRequest.id}
													onClick={() => runAction("deny")}>
													Deny
												</ActionButton>
												<ActionButton
													type="button"
													$variant="approve"
													disabled={
														actingId === selectedRequest.id ||
														!coordsOkForGoLive ||
														!chainReady.san
													}
													title={
														!coordsOkForGoLive
															? "Save latitude and longitude in Edit details first."
															: undefined
													}
													onClick={() => runAction("approve")}>
													{laHoSoChuoi(selectedRequest)
														? "Approve & send owner login"
														: "Approve & send login"}
												</ActionButton>
											</ActionWrap>
										</PermissionGate>
									</>
								) : (
									<>
										<ActionHint>
											{selectedRequest.status === StationRequestStatus.Accepted
												? "Approved. The temporary password lasts 7 days — send it again if the station has not signed."
												: "This request was denied."}
										</ActionHint>
										{/* Mật khẩu tạm sinh ra ở bước duyệt và chỉ sống 7 ngày. Hết hạn là đối
										    tác mất hẳn đường vào cổng — máy chủ đã có đường cấp lại từ trước,
										    nhưng tới 29/08/2026 màn này mới có nút gọi tới nó. */}
										{selectedRequest.status === StationRequestStatus.Accepted && (
											<PermissionGate permission="stations.manage">
												<ActionWrap style={{ marginTop: 12 }}>
													<ActionButton
														type="button"
														$variant="deny"
														style={{ borderColor: "#7c3aed", color: "#7c3aed" }}
														disabled={actingId === selectedRequest.id}
														title="Cấp mật khẩu tạm mới và gia hạn thêm 7 ngày"
														onClick={guiLaiMatKhau}>
														Resend password
													</ActionButton>
												</ActionWrap>
											</PermissionGate>
										)}
									</>
								)}
							</ActionBar>
							</>
						)}
					</DetailPanel>
				</MainGrid>
			</PageInner>
			{/* Thêm / sửa MỘT điểm của chuỗi. Mỗi điểm rồi thành một station thật nên hỏi
			    đủ tám ô — thiếu ô nào thì chính ô đó báo, không đợi tới lúc bấm duyệt. */}
			<Dialog
				open={diemDialogOpen}
				onClose={() => {
					if (diemDangLuu) return;
					setDiemDialogOpen(false);
				}}
				fullWidth
				maxWidth="sm">
				<DialogTitle>{chiSoDiem === null ? "Add location" : "Edit location"}</DialogTitle>
				<DialogContent>
					<div style={{ display: "grid", gap: 14, paddingTop: 8 }}>
						{diemLoi.__chung ? (
							<div style={{ color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
								{diemLoi.__chung}
							</div>
						) : null}
						{O_DIEM.map((o) =>
							o.khoa === "maps" ? (
								<div
									key={o.khoa}
									style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
									<TextField
										fullWidth
										variant="outlined"
										label={o.nhan}
										placeholder={o.goiY}
										value={diemDangSua.maps}
										onChange={(event) => {
											const giaTri = event.target.value;
											setDiemDangSua((prev) => ({ ...prev, maps: giaTri }));
											setDiemLoi((prev) => ({ ...prev, maps: "" }));
										}}
										error={Boolean(diemLoi.maps)}
										helperText={
											diemLoi.maps ||
											'Dán link rồi bấm "Trích từ Maps" để tự điền tên/địa chỉ/toạ độ/điện thoại.'
										}
									/>
									<Button
										type="button"
										variant="contained"
										disabled={dangTrichMaps}
										onClick={trichTuMaps}
										style={{ whiteSpace: "nowrap", marginTop: 8 }}>
										{dangTrichMaps ? "Đang trích…" : "Trích từ Maps"}
									</Button>
								</div>
							) : o.khoa === "nuoc" ? (
								<TextField
									key={o.khoa}
									fullWidth
									select
									variant="outlined"
									label={o.nhan}
									value={diemDangSua.nuoc}
									onChange={(event) => {
										setDiemDangSua((prev) => ({ ...prev, nuoc: event.target.value }));
										setDiemLoi((prev) => ({ ...prev, nuoc: "" }));
									}}
									error={Boolean(diemLoi.nuoc)}
									helperText={diemLoi.nuoc}>
									<MenuItem value="">Select country</MenuItem>
									{COUNTRIES.map((c) => (
										<MenuItem
											key={c.name}
											value={c.name}>
											{c.name}
										</MenuItem>
									))}
								</TextField>
							) : (
								<TextField
									key={o.khoa}
									fullWidth
									variant="outlined"
									label={o.nhan}
									placeholder={o.goiY}
									value={diemDangSua[o.khoa]}
									onChange={(event) => {
										const giaTri = event.target.value;
										setDiemDangSua((prev) => ({ ...prev, [o.khoa]: giaTri }));
										setDiemLoi((prev) => ({ ...prev, [o.khoa]: "" }));
									}}
									error={Boolean(diemLoi[o.khoa])}
									helperText={diemLoi[o.khoa]}
								/>
							),
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDiemDialogOpen(false)}
						disabled={diemDangLuu}>
						Cancel
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={luuDiem}
						disabled={diemDangLuu}>
						{diemDangLuu ? "Saving…" : chiSoDiem === null ? "Add location" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={editDialogOpen}
				onClose={() => {
					if (editSaving) return;
					setEditDialogOpen(false);
					setEditDialogError("");
					setEditErrors({});
				}}
				fullWidth
				maxWidth="md">
				<DialogTitle>Edit Station Request</DialogTitle>
				<DialogContent>
					{editState ? (
						<div style={{ display: "grid", gap: 16, paddingTop: 8 }}>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<TextField
									fullWidth
									variant="outlined"
									label="Business name"
									value={editState.businessName}
									onChange={(event) => updateEditField("businessName", event.target.value)}
									helperText="Legal / registered entity (optional if same as venue)"
								/>
								<TextField
									fullWidth
									variant="outlined"
									label="Hotel / Station name"
									value={editState.hotelName}
									onChange={(event) => updateEditField("hotelName", event.target.value)}
									error={Boolean(editErrors.hotelName)}
									helperText={editErrors.hotelName}
								/>
							</div>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<TextField
									fullWidth
									select
									variant="outlined"
									label="Business Type"
									value={editState.businessType}
									onChange={(event) => updateEditField("businessType", event.target.value)}
									error={Boolean(editErrors.businessType)}
									helperText={editErrors.businessType}>
									<MenuItem value="">Select business type</MenuItem>
									{STATION_REQUEST_EDIT_BUSINESS_TYPE_OPTIONS.map((option) => (
										<MenuItem
											key={option}
											value={option}>
											{option}
										</MenuItem>
									))}
								</TextField>
								<TextField
									fullWidth
									variant="outlined"
									label="Representative name"
									value={editState.representativeName}
									onChange={(event) => updateEditField("representativeName", event.target.value)}
									error={Boolean(editErrors.representativeName)}
									helperText={editErrors.representativeName}
								/>
							</div>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<TextField
									fullWidth
									variant="outlined"
									label="Contact position title"
									placeholder="e.g. Front Office Manager"
									value={editState.contactTitle}
									onChange={(event) => updateEditField("contactTitle", event.target.value)}
									error={Boolean(editErrors.contactTitle)}
									helperText={editErrors.contactTitle || "Required before approval"}
								/>
								<div />
							</div>
							{/* Chu chuoi khong co kho -> an anh kho cho ho so chuoi. */}
							{!laHoSoChuoi(selectedRequest) && (
							<div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
								<div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
									Storage area photos — required before approval
								</div>
								{editState.storageAreaImages.length ? (
									<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
										{editState.storageAreaImages.map((image) => (
											<div
												key={image}
												style={{ position: "relative" }}>
												<img
													src={image}
													alt="Storage area"
													style={{ width: 92, height: 68, objectFit: "cover", borderRadius: 6 }}
												/>
												<button
													type="button"
													onClick={() => xoaAnhKhuGuiDo(image)}
													style={{
														position: "absolute",
														top: -6,
														right: -6,
														width: 20,
														height: 20,
														borderRadius: "50%",
														border: 0,
														background: "#b91c1c",
														color: "#fff",
														cursor: "pointer",
														lineHeight: "20px",
														fontSize: 13,
													}}>
													×
												</button>
											</div>
										))}
									</div>
								) : (
									<div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 10 }}>
										No photo yet. Ask the hotel for one photo of where bags are kept, then upload it here.
									</div>
								)}
								<input
									type="file"
									accept="image/*"
									multiple
									disabled={dangTaiAnh}
									onChange={(event) => {
										void taiAnhKhuGuiDo(event.target.files);
										event.target.value = "";
									}}
								/>
								{dangTaiAnh ? (
									<span style={{ fontSize: 12.5, color: "#64748b", marginLeft: 8 }}>Uploading…</span>
								) : null}
							</div>
							)}
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<TextField
									fullWidth
									variant="outlined"
									label="Representative phone"
									value={editState.representativePhone}
									onChange={(event) => updateEditField("representativePhone", event.target.value)}
									error={Boolean(editErrors.representativePhone)}
									helperText={editErrors.representativePhone}
								/>
								<TextField
									fullWidth
									variant="outlined"
									label="Email (bookings)"
									value={editState.ownerEmail}
									onChange={(event) => updateEditField("ownerEmail", event.target.value)}
									error={Boolean(editErrors.ownerEmail)}
									disabled={Boolean(isCreateAccount)}
									helperText={
										editErrors.ownerEmail ||
										(isCreateAccount
											? "This login email is locked because the account was already pre-created on submit."
											: "")
									}
								/>
							</div>
							{/* Chu chuoi la phap nhan khong co dia diem rieng -> an nhom field vi tri (toa do/dia chi/nuoc/thanh pho/gio) cho ho so chuoi; cac field nay thuoc tung diem con (modal Add location). */}
							{!laHoSoChuoi(selectedRequest) && (
							<>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<Autocomplete
									options={countryOptions}
									value={editState.country || null}
									onChange={(_e: any, value: string | null) => {
										updateEditField("country", value || "");
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="outlined"
											label="Country"
											error={Boolean(editErrors.country)}
											helperText={editErrors.country}
										/>
									)}
								/>
								<Autocomplete
									options={cityOptions}
									value={editState.cityName || null}
									onChange={(_e: any, value: string | null) => {
										const city = value || "";
										const matchedDest = destinations.find(
											(d) => d.name === city && d.country === editState.country
										);
										setEditState((prev) =>
											prev
												? {
														...prev,
														cityName: city,
														destinationId: matchedDest?.id || "",
												  }
												: prev
										);
										setEditErrors((prev) => ({ ...prev, destinationId: "" }));
									}}
									disabled={!editState.country}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="outlined"
											label="City"
											error={Boolean(editErrors.destinationId)}
											helperText={editErrors.destinationId}
										/>
									)}
								/>
							</div>
							<TextField
								fullWidth
								variant="outlined"
								label="Address"
								value={editState.address}
								onChange={(event) => updateEditField("address", event.target.value)}
								error={Boolean(editErrors.address)}
								helperText={editErrors.address}
							/>
							<TextField
								fullWidth
								variant="outlined"
								label="Google Map Link"
								value={editState.mapLink}
								onChange={(event) => updateEditField("mapLink", event.target.value)}
								error={Boolean(editErrors.mapLink)}
								helperText={editErrors.mapLink}
							/>
							<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
								<TextField
									fullWidth
									variant="outlined"
									label="Latitude"
									value={editState.lat}
									onChange={(event) => updateEditField("lat", event.target.value)}
									placeholder="e.g. 21.0285"
									error={Boolean(editErrors.lat)}
									helperText={editErrors.lat || "Decimal degrees — required before Go live."}
								/>
								<TextField
									fullWidth
									variant="outlined"
									label="Longitude"
									value={editState.long}
									onChange={(event) => updateEditField("long", event.target.value)}
									placeholder="e.g. 105.8542"
									error={Boolean(editErrors.long)}
									helperText={editErrors.long}
								/>
							</div>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "160px 1fr 1fr",
									gap: 12,
									alignItems: "start",
								}}>
								<TextField
									fullWidth
									select
									variant="outlined"
									label="Open 24 hours"
									value={editState.openAllDay ? "yes" : "no"}
									onChange={(event) => updateEditField("openAllDay", event.target.value === "yes")}>
									<MenuItem value="no">No</MenuItem>
									<MenuItem value="yes">Yes</MenuItem>
								</TextField>
								<TextField
									fullWidth
									variant="outlined"
									type="time"
									label="Opening from"
									InputLabelProps={{ shrink: true }}
									value={editState.openFrom}
									onChange={(event) => updateEditField("openFrom", event.target.value)}
									error={Boolean(editErrors.openFrom)}
									helperText={editErrors.openFrom}
									disabled={editState.openAllDay}
								/>
								<TextField
									fullWidth
									variant="outlined"
									type="time"
									label="Opening to"
									InputLabelProps={{ shrink: true }}
									value={editState.openTo}
									onChange={(event) => updateEditField("openTo", event.target.value)}
									error={Boolean(editErrors.openTo)}
									helperText={editErrors.openTo}
									disabled={editState.openAllDay}
								/>
							</div>
							</>
							)}
							{editDialogError ? (
								<Typography style={{ color: "#dc2626", lineHeight: 1.7 }}>{editDialogError}</Typography>
							) : null}
						</div>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setEditDialogOpen(false);
							setEditDialogError("");
							setEditErrors({});
						}}
						disabled={editSaving}>
						Cancel
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleSaveEdit}
						disabled={editSaving}>
						{editSaving ? "Saving..." : "Save changes"}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={denyDialogOpen}
				onClose={() => {
					if (actingId) return;
					setDenyDialogOpen(false);
					setDenyReason("");
					setDenyDialogError("");
				}}
				fullWidth
				maxWidth="xs">
				<DialogTitle>Deny Station Request</DialogTitle>
				<DialogContent>
					<Typography style={{ color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
						This request will be marked as denied and the partner will receive this reason by email.
					</Typography>
					<TextField
						fullWidth
						multiline
						rows={4}
						variant="outlined"
						label="Reason for Denial"
						placeholder="Explain why the request is being denied."
						value={denyReason}
						onChange={(event) => {
							setDenyReason(event.target.value);
							if (denyDialogError) {
								setDenyDialogError("");
							}
						}}
						required
						error={Boolean(denyDialogError)}
						helperText={denyDialogError || "This reason will be sent to the station in the denial email."}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDenyDialogOpen(false);
							setDenyReason("");
							setDenyDialogError("");
						}}
						disabled={Boolean(actingId)}>
						Cancel
					</Button>
					<Button
						color="secondary"
						onClick={handleConfirmDeny}
						disabled={Boolean(actingId) || !denyReason.trim()}>
						{actingId && selectedRequest?.id === actingId ? "Processing..." : "Deny"}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={Boolean(previewImageUrl)}
				onClose={() => {
					setPreviewImageUrl(null);
					setPreviewImageLabel("");
				}}
				maxWidth={false}
				PaperProps={{
					style: {
						background: "transparent",
						boxShadow: "none",
						overflow: "visible",
					},
				}}>
				<DialogContent
					style={{
						padding: 0,
						background: "transparent",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}>
					{previewImageUrl ? (
						<PreviewImage
							src={previewImageUrl}
							alt={previewImageLabel || "Image Preview"}
						/>
					) : null}
				</DialogContent>
			</Dialog>
		</PageWrap>
	);
}
